import { ChevronDown, Plus } from 'lucide-react';
import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { NewSlide, useSlidesStore } from '../../../context/zustand/slidesStore';
import { useAuth } from '../../../hooks/useAuth';
import { usePorteeSlideManager, usePotree } from '../../../hooks/usePotree';
import { useToggle } from '../../../hooks/useToggle';
import { OrtoScene } from '../../../types';
import { OrtoSceneHelper } from '../../../utils/OrtoSceneHelper';
import { authTokenHeader } from '../../../utils/helper';
import { Accordion, AccordionItem } from '../../shadcn-ui/accordion';
import { Button } from '../../shadcn-ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../shadcn-ui/collapsible';
import { DialogTrigger } from '../../shadcn-ui/dialog';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from '../../shadcn-ui/sidebar';
import Presentation from '../scenes/Presentation';
import SlideEditor, {
  SlideEditorOnChange,
  editedSlideHasErrors,
} from '../scenes/SlideEditor';
import Authenticated from '../shared/Authenticated';
import Wrapper from '../shared/Wrapper';

const RightSidebar = () => {
  const { token } = useAuth();
  const { potreeViewer, project, setProject } = usePotree();
  const potreeSlideManager = usePorteeSlideManager();
  const { potreeDisplayMode } = useToggle();

  const state = useSlidesStore();
  const { newSlide, setNewSlide, resetNewSlide } = state;

  const _handleSlideChange = useCallback<SlideEditorOnChange>(
    (key, value) => {
      if (key === 'transitionDuration') {
        const slideCopy = { ...newSlide };
        slideCopy.json_data[0].value.view.transitionDuration = value as number;
        setNewSlide(slideCopy);
      } else {
        setNewSlide({ ...newSlide, [key]: value });
      }
    },
    [newSlide, setNewSlide]
  );

  if (!potreeViewer) return null;

  const displaySlides = project?.field_scenes[potreeDisplayMode]?.scenes ?? [];

  // Create a presentations matrix, i.e. an array of presentations (which in turn are slide arrays)
  const presentations: OrtoScene[][] = [];

  // NOTE backwards compatibility hack for the even bigger hack below
  // -> if no slide name starts with a '#', then the project has no presentations and should be rendered as before
  const numberOfPresentations = displaySlides.filter((slide) =>
    slide.label.startsWith('#')
  ).length;
  if (numberOfPresentations === 0) {
    presentations.push(displaySlides);
  } else {
    // NOTE this is a HACK to iron over the fact, that we currently have NO presentation entity on a backend-/API-level
    displaySlides.forEach((slide, i) => {
      if (slide.label.startsWith('#') || i === 0) {
        presentations.push([slide]);
      } else {
        const j = Math.max(presentations.length - 1, 0);
        presentations[j].push(slide);
      }
    });
  }

  const addSlide = async (slide: NewSlide) => {
    const body: any = JSON.stringify(
      OrtoSceneHelper.getScene(
        slide.field_scene_type,
        potreeViewer,
        slide.label,
        slide.field_description,
        slide.json_data[0]?.value?.view?.transitionDuration,
        {
          annotationOptions: {
            discard: !slide.copyAnnotations, // NOTE: if the user does not want to copy the existing annotations, discard them.
            resetIDs: true, // NOTE: Resets existing annotation IDs. This is a hack, necessary because the potreeViewer-content is basically carried over into the new slide (incluing IDs)...
          },
        }
      )
    );
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/project/${project?.id}/scene`,
        {
          method: 'PUT',
          headers: authTokenHeader(token),
          body,
        }
      );

      const newScene = await response.json();

      if (!newScene || !response.ok) {
        toast.error(`Adding scene '${newScene.label}' failed.`);
        return;
      }

      toast.success(`Scene '${newScene.label}' added.`);

      setProject((prevProject: any) => ({
        ...prevProject,
        field_scenes: {
          ...prevProject.field_scenes,
          [potreeDisplayMode]: {
            ...prevProject.field_scenes[potreeDisplayMode],
            scenes: [
              ...prevProject.field_scenes[potreeDisplayMode].scenes,
              newScene,
            ],
          },
        },
      }));

      return newScene;
    } catch (error: any) {
      console.log('API request failed:', error.message);
      throw Error(error.message);
    }
  };

  const _handleSlideEditorSubmit = async (slide: NewSlide) => {
    if (editedSlideHasErrors(slide)) return;

    // Add new slide (aka scene) to project
    const newSlide = await addSlide(slide);

    // Trigger navigation to the new slide
    potreeSlideManager.goToSlide(newSlide, potreeDisplayMode);
  };

  return (
    <Sidebar side="right" className="max-w-[270px] border-none">
      {/* <SidebarHeader></SidebarHeader> */}
      <SidebarContent>
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger>
                Slides
                {/* TODO use different icon? I.e. not from lucide-react */}
                <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent className="mt-2 p-2">
                <Accordion type="multiple">
                  {presentations.map((slides, index) => (
                    // NOTE: the value should be the presentation ID, current hacky solution via slides[0].label
                    <AccordionItem
                      key={`presentation-${index + 1}`}
                      value={slides[0]?.label ?? `presentation-${index + 1}`}
                      asChild
                    >
                      <Presentation slides={slides} order={`${index + 1}`} />
                    </AccordionItem>
                  ))}
                </Accordion>

                <Authenticated>
                  <Wrapper classNames="relative mt-3 mb-6">
                    <SlideEditor
                      slideNumber={`${displaySlides.length + 1}`}
                      slide={newSlide}
                      onSlideChange={_handleSlideChange}
                      onCancel={resetNewSlide}
                      onSubmit={() => _handleSlideEditorSubmit(newSlide)}
                      submitLabel="create"
                      showCopyAnnotationsCheckbox
                    >
                      <DialogTrigger asChild>
                        <Button
                          size="icon"
                          type="button"
                          variant="outline"
                          className="light"
                          onClick={() => {
                            setNewSlide({
                              label: '',
                              // @ts-ignore
                              json_data: OrtoSceneHelper.getScene(
                                potreeDisplayMode,
                                potreeViewer,
                                ''
                              ).json_data,
                              field_scene_type: potreeDisplayMode,
                            });
                          }}
                        >
                          <Plus size={20} />
                        </Button>
                      </DialogTrigger>
                    </SlideEditor>
                  </Wrapper>
                </Authenticated>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>
      {/* <SidebarFooter ></SidebarFooter> */}
    </Sidebar>
  );
};

export default RightSidebar;
